import { useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Link } from "wouter";
import type { ClaimV2, RAGSourceV2, SupportLevel } from "@/lib/api";

interface ClaimsListProps {
  claims: ClaimV2[];
  sources: RAGSourceV2[];
}

/**
 * Couleurs par support_level (CdC #2 + #5 guardrails).
 *
 * `explicit` = vert (citation literale) ; `derivative` = bleu
 * (inférence) ; `ambiguous` = orange (interprétatif) ; `unsupported`
 * = rouge (alerte critique — le critique layer Lot 3 a dû intervenir).
 */
const SUPPORT_COLORS: Record<SupportLevel, { fg: string; bg: string; label: string }> = {
  explicit: {
    fg: "var(--block-green)",
    bg: "color-mix(in oklch, var(--block-green) 14%, transparent)",
    label: "explicite",
  },
  derivative: {
    fg: "var(--ai-violet)",
    bg: "color-mix(in oklch, var(--ai-violet) 14%, transparent)",
    label: "dérivé",
  },
  ambiguous: {
    fg: "var(--alert-orange)",
    bg: "color-mix(in oklch, var(--alert-orange) 14%, transparent)",
    label: "ambigu",
  },
  unsupported: {
    fg: "var(--block-red)",
    bg: "color-mix(in oklch, var(--block-red) 14%, transparent)",
    label: "non sourcé",
  },
};

const LEGAL_CATEGORY_LABELS: Record<string, string> = {
  droit_positif: "Droit positif",
  jurisprudence: "Jurisprudence",
  doctrine: "Doctrine",
  note_interne: "Note interne",
};

/**
 * Liste des claims atomiques (CdC #2). Chaque claim est cliquable :
 * l'expansion révèle les sources mobilisées avec leur extrait textuel,
 * permettant la vérification visuelle de la citation.
 *
 * Pas de Sheet/Modal : l'affichage in-line conserve le contexte de
 * lecture. Pour un panneau latéral sticky, voir Lot 7.bis (UX juriste
 * expérimenté).
 */
export function ClaimsList({ claims, sources }: ClaimsListProps) {
  if (claims.length === 0) {
    return null;
  }

  return (
    <section aria-label="Assertions juridiques et sources">
      <h3
        className="flex items-center gap-2 text-caption-arbitre mb-3"
        style={{ color: "var(--proof-gold)" }}
      >
        <BookOpen size={12} />
        Assertions juridiques ({claims.length})
      </h3>
      <ul className="space-y-2">
        {claims.map((claim) => (
          <ClaimItem key={claim.claim_id} claim={claim} sources={sources} />
        ))}
      </ul>
    </section>
  );
}

function ClaimItem({
  claim,
  sources,
}: {
  claim: ClaimV2;
  sources: RAGSourceV2[];
}) {
  const [expanded, setExpanded] = useState(false);
  const support = SUPPORT_COLORS[claim.support_level] ?? SUPPORT_COLORS.ambiguous;
  const linkedSources = sources.filter((s) => claim.source_ids.includes(s.id));
  const hasDetail = linkedSources.length > 0 || claim.quote;
  // Phase 6 — un claim `unsupported` ne doit PAS exposer de lien corpus
  // cliquable. Le backend met deja `corpus_links = []` (Phase 5), le frontend
  // doit respecter le meme contrat pour ne pas afficher de lien trompeur.
  // L'extrait textuel reste visible (audit AI Act Art. 13).
  const isUnsupported = claim.support_level === "unsupported";

  return (
    <li
      className="rounded-md p-3 border"
      style={{
        borderColor: "var(--graphite-600)",
        background: "var(--graphite-800)",
      }}
    >
      <div className="flex items-start gap-2">
        <span
          className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono"
          style={{
            background: "var(--graphite-700)",
            color: "var(--arbitre-slate-300)",
          }}
        >
          {claim.claim_id}
        </span>
        <p
          className="text-sm flex-1"
          style={{ color: "var(--arbitre-slate-100)", lineHeight: 1.55 }}
        >
          {claim.text}
        </p>
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span
          className="text-[10px] px-1.5 py-0.5 rounded"
          style={{ color: support.fg, background: support.bg }}
          aria-label={`Niveau de support : ${support.label}`}
        >
          {support.label}
        </span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded"
          style={{
            color: "var(--arbitre-slate-300)",
            background: "var(--graphite-700)",
          }}
        >
          {LEGAL_CATEGORY_LABELS[claim.legal_category] ?? claim.legal_category}
        </span>
        {claim.source_ids.length > 0 && (
          <span
            className="text-[10px]"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            {claim.source_ids.length} source{claim.source_ids.length > 1 ? "s" : ""}
          </span>
        )}
        {hasDetail && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[10px] ml-auto flex items-center gap-0.5 hover:underline"
            style={{ color: "var(--ai-violet)" }}
            aria-expanded={expanded}
            aria-controls={`claim-${claim.claim_id}-detail`}
          >
            {expanded ? (
              <>
                Replier <ChevronUp size={10} />
              </>
            ) : (
              <>
                Détails <ChevronDown size={10} />
              </>
            )}
          </button>
        )}
      </div>
      {expanded && (
        <div
          id={`claim-${claim.claim_id}-detail`}
          className="mt-3 pt-3 border-t space-y-2"
          style={{ borderColor: "var(--graphite-600)" }}
        >
          {claim.quote && (
            <blockquote
              className="text-xs italic pl-3"
              style={{
                color: "var(--arbitre-slate-200)",
                borderLeft: "2px solid var(--proof-gold)",
                lineHeight: 1.55,
              }}
            >
              « {claim.quote} »
            </blockquote>
          )}
          {linkedSources.map((source) => {
            const headerContent = (
              <>
                {source.source}
                {source.jurisdiction && ` · ${source.jurisdiction}`}
                {source.legal_force && ` · ${source.legal_force}`}
              </>
            );
            return (
              <div
                key={source.id}
                className="text-[11px] p-2 rounded"
                style={{
                  background: "var(--graphite-700)",
                  color: "var(--arbitre-slate-300)",
                  lineHeight: 1.55,
                }}
              >
                {/* Phase 6 — pour un claim `unsupported`, on garde la
                    transparence sur l'extrait evalue (audit AI Act Art. 13)
                    MAIS on supprime le lien cliquable vers /corpus/<id> :
                    le backend met deja `claim.corpus_links = []` pour les
                    `unsupported` (computed_field Phase 5). Afficher un
                    lien cliquable serait trompeur — le claim a explicitement
                    ete classe comme non supporte par cette source. */}
                {isUnsupported ? (
                  <code
                    className="gsf-mono-proof inline-flex items-center gap-1 mb-1 text-[10px]"
                    style={{ color: "var(--arbitre-slate-500)" }}
                    aria-label={`Source ${source.source} evaluee mais non retenue pour ce claim`}
                    data-testid={`corpus-static-${source.id}`}
                  >
                    {headerContent}
                  </code>
                ) : (
                  <Link
                    href={`/corpus/${source.id}`}
                    aria-label={`Voir la source ${source.source} dans le corpus`}
                    data-testid={`corpus-link-${source.id}`}
                    className="gsf-mono-proof inline-flex items-center gap-1 mb-1 text-[10px] hover:underline"
                    style={{ color: "var(--proof-gold)" }}
                  >
                    {headerContent}
                    <ExternalLink size={9} aria-hidden="true" />
                  </Link>
                )}
                <div className="block mt-1">{source.text}</div>
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
}
